// fetchConfigurationsBootstrap.js
'use strict';

import getMeData from '../selector/getMeData.js';
import getConfigData from '../selector/getConfigData.js';

import setConfigurations from '../action/setConfigurations.js';
import setABTestConfig from '../action/setABTestConfig.js';
import mergeMeData from '../action/mergeMeData.js';
import {
  initGrowthBookConfig,
  updateGrowthBookAttribute,
} from '../action/configActions.js';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import i18n, { localeConvertor } from '../resource/i18n.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import { reduxStoreInitializePromise } from '../resource/reduxStoreInitializePromise.js';
import {
  CONFIG_PRIORITY_PRESENCE_USER,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
} from '../resource/configPriority.js';
import { GrowthBookInitStatus } from '../resource/configConstants.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import { setRemoteMeta } from '../resource/mixpanel.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  SET_CONFIG_DATA,
} from '../ActionTypes.js';

/**
 * Fetch configurations bootstrap
 * @kind action
 * @param {string} {priority} - config priority.
 * @param {string} [{language}] - language to request configurations for.
 *   Defaults to the current i18n language; pass it explicitly while the
 *   language is changing, since `i18n.changeLanguage` resolves asynchronously
 *   and `i18n.language` would still hold the previous value.
 * @return {Promise} Action promise.
 */
const fetchConfigurationsBootstrap =
  ({ priority, language } = {}) =>
  async (dispatch, getState) => {
    // guarantee have the latest store
    await reduxStoreInitializePromise;

    const networkingSelectPath = ['fetch', 'bootstrap', priority];
    const token = getMeData(getState(), 'token');

    if (CONFIG_PRIORITY_PRESENCE_USER === priority && !token) {
      return dispatch({ type: '' });
    }

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        ...(CONFIG_PRIORITY_PRESENCE_USER === priority
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {}),
      },
    };

    const fetchUrl = getResourceUrl({ endpoint: '/configurations/bootstrap' });
    // `lang` takes precedence over the request's `Accept-Language` server-side,
    // so the response reflects the in-app language instead of the browser one:
    // the `language=<lang>` configuration group, the GrowthBook `language`
    // attribute and the Mixpanel language meta.
    const requestLanguage = language || i18n.language;
    if (requestLanguage) {
      fetchUrl.searchParams.set(
        'lang',
        localeConvertor({ locale: requestLanguage, isISO639: true })
      );
    }

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
          shouldIgnoreHeaderAuthorizationOnRetryFetch:
            CONFIG_PRIORITY_PRESENCE_CLIENT === priority,
        });
      }
      const payload = (await response.json()) || {};

      const attribute =
        payload?.growthbook?.['user-attributes'] || EMPTY_OBJECT;
      dispatch({
        type: SET_CONFIG_DATA,
        payload: {
          selectPath: [
            'attribute',
            CONFIG_PRIORITY_PRESENCE_USER === priority ? 'user' : 'client',
          ],
          data: attribute,
        },
      });

      if (
        (!!token && priority === CONFIG_PRIORITY_PRESENCE_USER) ||
        (!token && priority === CONFIG_PRIORITY_PRESENCE_CLIENT)
      ) {
        const initStatus = getConfigData(getState(), ['status', 'initStatus']);
        if (initStatus === GrowthBookInitStatus.SUCCESS) {
          dispatch(updateGrowthBookAttribute({ attribute }));
        } else {
          dispatch(initGrowthBookConfig({ attribute }));
        }
      }

      await dispatch(
        setABTestConfig({
          abToken: payload.ab,
          priority,
        })
      );
      if (payload['configuration-group-id']) {
        await dispatch(
          setConfigurations({
            configData: {
              'configuration-group-id': payload['configuration-group-id'],
              'configuration-groups': payload['configuration-groups'],
            },
            priority,
          })
        );
      }
      if (payload['mixpanel']) {
        setRemoteMeta({ data: payload['mixpanel'] });
      }
      const userCountry = payload?.metadata?.country;
      if (userCountry) {
        dispatch(mergeMeData({ field: 'country', value: userCountry }));
        dispatch(
          mergeMeData({
            field: 'defaultCountryCode',
            value: userCountry.toUpperCase(),
          })
        );
      }
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchConfigurationsBootstrap;
